import { useCallback, useEffect, useRef, useState } from 'react';

export type DialogSubmissionResult<T> =
  | { ok: true; value: T; current: boolean }
  | { ok: false; error: unknown; current: boolean };

/**
 * One request at a time for each opening of a create dialog.
 *
 * The guard is a ref: two Enter presses in the same frame both see React
 * state from before the first one, so a state flag lets both through and
 * creates two documents.
 *
 * A request that finishes after its dialog was closed, or closed and opened
 * again, comes back with `current: false`. The caller then must not close
 * the dialog or clear its fields, which now belong to the newer opening.
 * `pending` is true only for a request from the current opening, so a
 * reopened dialog doesn't show "Creating…" for the old one.
 */
export function useDialogSubmission(open: boolean) {
  const openingRef = useRef(0);
  const openRef = useRef(open);
  const inFlightRef = useRef<number | null>(null);
  const [opening, setOpening] = useState(0);
  const [pendingOpening, setPendingOpening] = useState<number | null>(null);

  useEffect(() => {
    openRef.current = open;
    if (!open) return;
    openingRef.current += 1;
    setOpening(openingRef.current);
  }, [open]);

  const submit = useCallback(
    async <T,>(
      run: () => Promise<T>
    ): Promise<DialogSubmissionResult<T> | null> => {
      const mine = openingRef.current;
      if (inFlightRef.current === mine) return null;
      inFlightRef.current = mine;
      setPendingOpening(mine);
      const isCurrent = () => mine === openingRef.current && openRef.current;
      try {
        const value = await run();
        return { ok: true, value, current: isCurrent() };
      } catch (error) {
        return { ok: false, error, current: isCurrent() };
      } finally {
        if (inFlightRef.current === mine) inFlightRef.current = null;
        setPendingOpening((pending) => (pending === mine ? null : pending));
      }
    },
    []
  );

  return { submit, pending: pendingOpening !== null && pendingOpening === opening };
}
